import {Checkbox, FilterIcon, PopoverButton} from '@kit';

import React from 'react';

import {MapLabelFragment} from '../../../generated/types';
import {DEPTHS, DEPTH_COLORS, DEPTH_TITLES, MARKER_KINDS, MARKER_TITLES, MarkerShape, isMarkerKind} from './map-symbols';

import css from './map-view.module.css';

type TextGroup = {key: string; className: string; kinds: string[]; title: string; sample: string};

const TEXT_GROUPS: TextGroup[] = [
	{key: 'realm', className: 'kind-realm', kinds: ['realm'], title: 'Страны и области', sample: 'Амн'},
	{key: 'district', className: 'kind-district', kinds: ['district'], title: 'Районы', sample: 'Истфей'},
	{key: 'building', className: 'kind-building', kinds: ['building'], title: 'Здания и заведения', sample: 'Портовая больница'},
	{key: 'place', className: 'kind-place', kinds: ['place'], title: 'Места', sample: 'Поле Триумфа'},
	{key: 'street', className: 'kind-street', kinds: ['street'], title: 'Улицы', sample: 'Медная ул.'},
	{key: 'gate', className: 'kind-gate', kinds: ['gate'], title: 'Ворота', sample: 'Утёсные ворота'},
	{key: 'water', className: 'kind-sea', kinds: ['sea', 'lake', 'river', 'water'], title: 'Моря, озёра, реки', sample: 'Лунное море'},
	{key: 'forest', className: 'kind-forest', kinds: ['forest'], title: 'Леса', sample: 'Высокий лес'},
	{key: 'terrain', className: 'kind-mountains', kinds: ['mountains', 'hills', 'terrain', 'desert', 'glacier', 'swamp'], title: 'Горы, холмы, равнины', sample: 'Хребет Мира'},
	{key: 'road', className: 'kind-road', kinds: ['road'], title: 'Дороги и перевалы', sample: 'Торговый Путь'},
];

const ROAD_KINDS: Array<{kind: string; title: string; sample: React.ReactNode}> = [
	{
		kind: 'road',
		title: 'Большие дороги',
		sample: (
			<svg width="28" height="10" aria-hidden="true">
				<line x1="2" y1="5" x2="26" y2="5" stroke="#fff3d6" strokeWidth="6" strokeLinecap="round" />
				<line x1="2" y1="5" x2="26" y2="5" stroke="#c8161d" strokeWidth="3" strokeLinecap="round" />
			</svg>
		),
	},
	{
		kind: 'track',
		title: 'Тропы',
		sample: (
			<svg width="28" height="10" aria-hidden="true">
				<line x1="3" y1="5" x2="26" y2="5" stroke="#c8161d" strokeWidth="3" strokeLinecap="round" strokeDasharray="0 6" />
			</svg>
		),
	},
];

/** ключ объекта в фильтре: подписи со значком фильтруются по значку, остальные — по виду подписи */
export function filterKey(label: Pick<MapLabelFragment, 'kind' | 'marker' | 'markerX' | 'layer'>): string | null {
	if (label.layer === 'underdark') return null;
	if (label.markerX != null && isMarkerKind(label.marker)) return `m:${label.marker}`;
	const group = TEXT_GROUPS.find((item) => item.kinds.includes(label.kind));
	return group ? `t:${group.key}` : null;
}

export const roadFilterKey = (kind: string) => `r:${kind}`;

type Props = {
	labels: MapLabelFragment[];
	/** виды линий дорог на карте */
	roadKinds: Set<string>;
	/** скрытые ключи фильтра */
	hidden: Set<string>;
	onToggle: (key: string) => void;
	underdark: boolean;
};

export const MapFilter: React.FC<Props> = ({labels, roadKinds, hidden, onToggle, underdark}) => {
	const keys = new Set(labels.map(filterKey).filter(Boolean));
	const markers = MARKER_KINDS.filter((kind) => keys.has(`m:${kind}`));
	const roads = ROAD_KINDS.filter((road) => roadKinds.has(road.kind));
	const texts = TEXT_GROUPS.filter((group) => keys.has(`t:${group.key}`));
	const hasDepths = labels.some((label) => label.layer === 'underdark');

	const row = (key: string, chip: React.ReactNode, title: string) => (
		<Checkbox
			key={key}
			checked={!hidden.has(key)}
			onChange={() => onToggle(key)}
			label={
				<span className={css.filterRow}>
					<span className={css.legendChip}>{chip}</span>
					{title}
				</span>
			}
		/>
	);

	if (!markers.length && !roads.length && !texts.length && !hasDepths) return null;

	return (
		<PopoverButton
			appearance={hidden.size ? 'primary' : 'subtle'}
			icon={<FilterIcon />}
			title="Объекты на карте"
			aria-label="Объекты на карте"
			popover={
				<div className={css.filterMenu}>
					{markers.length > 0 && <div className={css.filterGroup}>{markers.map((kind) => row(`m:${kind}`, <MarkerShape kind={kind} size={18} />, MARKER_TITLES[kind]))}</div>}
					{roads.length > 0 && <div className={css.filterGroup}>{roads.map((road) => row(roadFilterKey(road.kind), road.sample, road.title))}</div>}
					{texts.length > 0 && (
						<div className={css.filterGroup}>
							{texts.map((group) => row(`t:${group.key}`, <span className={`${css.legendText} ${css[group.className] || ''}`}>{group.sample}</span>, group.title))}
						</div>
					)}
					{underdark && hasDepths && (
						<ul className={css.legendList}>
							{DEPTHS.map((depth) => (
								<li key={depth}>
									<span className={css.legendSwatch} style={{borderColor: DEPTH_COLORS[depth]}}>
										<svg width="28" height="16" aria-hidden="true">
											<rect width="28" height="16" fill={`url(#map-depth-${depth})`} />
										</svg>
									</span>
									{DEPTH_TITLES[depth]}
								</li>
							))}
						</ul>
					)}
				</div>
			}
		/>
	);
};
